
import Vue from 'vue';
import DateRange from '~shared/components/selectors/DateRangeQueryParamSelector.vue';
import QueryParamButtonGroup from '~shared/components/selectors/QueryParamButtonGroup.vue';
import GroupSelector from '~shared/components/selectors/GroupQueryParamSelector.vue';
import CampaignSelector from '~shared/components/selectors/CampaignQueryParamSelector.vue';
import Overview from '../../components/advertiser/Overview.vue';
import StatisticsTable from '../../components/advertiser/StatisticsTable.vue';
import CategoryReport from '../../components/advertiser/CategoryReport.vue';
import GeoReport from '../../components/advertiser/GeoReport.vue';
import DeviceReport from '../../components/advertiser/DeviceReport.vue';
import DomainReport from '../../components/advertiser/DomainReport.vue';
import { IGroup, ICampaign } from '../../store/advertiser-insights';
import gql from 'graphql-tag';
import { normalizeDateRange } from '~shared/utils';
import { handleGraphQLErrors } from '~shared/utils/graphql-helper';
import Moment from 'moment/moment';

interface ITab {
  title: string;
  active: boolean;
  show: boolean;
}

interface IData {
  profile: string;
  isInitialPageLoad: boolean;
  tabs: ITab[];
  activeTab: string;
  missingCampaigns: boolean;
  marketplaceDomain: string;
  startDate: string;
  endDate: string;
  inputGroupBy: string;
  selectedAdIds: string[];
  selectedAdSetIds: string[];
  downloadingReport: boolean;
  includeArchivedCampaigns: boolean;
  isNoData: boolean;
}

export default Vue.extend({
  components: {
    DateRange,
    QueryParamButtonGroup,
    GroupSelector,
    CampaignSelector,
    Overview,
    StatisticsTable,
    CategoryReport,
    GeoReport,
    DeviceReport,
    DomainReport,
  },
  data(): IData {
    return {
      profile: this.$store.state.auth.user,
      marketplaceDomain: process.env.marketplaceDomain || '',
      isInitialPageLoad: true,
      missingCampaigns: false,
      startDate: '',
      endDate: '',
      inputGroupBy: '',
      downloadingReport: false,
      selectedAdIds: [],
      selectedAdSetIds: [],
      includeArchivedCampaigns: false,
      activeTab: 'OVERVIEW',
      tabs: [
        {
          title: 'OVERVIEW',
          active: true,
          show: true,
        },
        {
          title: 'CATEGORY',
          active: false,
          show: true,
        },
        {
          title: 'GEO',
          active: false,
          show: false,
        },
        {
          title: 'DEVICE',
          active: false,
          show: true,
        },
        {
          title: 'DOMAIN',
          active: false,
          show: false,
        },
      ],
      isNoData: false,
    };
  },
  computed: {
    // Currently selected Group in filter
    selectedGroup(): IGroup | null {
      return this.$store.state['advertiser-insights'].filters.selectedGroup;
    },
    // Currently selected Campaign in filter
    selectedCampaign(): ICampaign | null {
      return this.$store.state['advertiser-insights'].filters.selectedCampaign;
    },
    // Currently selected date range in filter
    selectedDateRange(): any | null {
      return this.$store.state['advertiser-insights'].filters.selectedDateRange;
    },
    selectedGroupBy(): string {
      return this.$store.state['advertiser-insights'].filters.selectedGroupBy;
    },
    selectedResolution(): string {
      return this.$store.state['advertiser-insights'].filters
        .selectedResolution;
    },
    selectedChildIds(): [] {
      return this.$store.state['advertiser-insights'].filters.selectedChildIds;
    },
    // Currently selected date range in filter
    activeDateRangeHumanReadable(): string | null {
      return this.$store.state['advertiser-insights'].activeFilters.meta
        .selectedDateRangeHumanReadable;
    },
    // Currently active (applied) group by
    activeGroupBy(): string {
      return this.$store.state['advertiser-insights'].activeFilters
        .selectedGroupBy;
    },
    // The ID of the currently selected id
    campaignId(): string | null {
      const selectedCampaign = this.selectedCampaign;
      if (!selectedCampaign || !selectedCampaign.id) {
        return null;
      }
      return selectedCampaign.id;
    },
    lastUpdated(): Moment.Moment | null {
      return this.$store.state['advertiser-insights'].lastUpdated;
    },
  },
  watch: {
    '$route.query.view': {
      handler(value) {
        if (!value) {
          this.setTab('OVERVIEW');
        } else if (value.toUpperCase() === 'GEO') {
          this.setTab('GEO');
        } else if (value.toUpperCase() === 'CATEGORY') {
          this.setTab('CATEGORY');
        } else if (value.toUpperCase() === 'DEVICE') {
          this.setTab('DEVICE');
        } else if (value.toUpperCase() === 'DOMAIN') {
          this.setTab('DOMAIN');
        }
      },
      immediate: true,
    },
    selectedCampaign: {
      handler(val): void {
        if (!val || !val.id) {
          // If no campaign is selected only display overview
          this.setTab('OVERVIEW');
          this.tabs.forEach((t) => {
            if (t.title !== 'OVERVIEW') {
              t.show = false;
            }
          });
          return;
        } else {
          // Show all tabs
          this.tabs.forEach((t) => {
            // Fix to prevent domain to be displayed by default.
            // Domain report is controlled on group level
            if (
              t.title === 'DOMAIN' &&
              this.selectedGroup &&
              this.selectedGroup.reportSettings &&
              !this.selectedGroup.reportSettings.domain.length
            ) {
              t.show = false;
            } else {
              t.show = true;
            }
          });
        }

        // Handle if we should display geo tab
        if (val.provider && val.provider === 'smart') {
          const geoTab = this.tabs.find((t) => t.title === 'GEO');
          if (geoTab) {
            geoTab.show = true; // display geo tab
          }
        } else {
          const geoTab = this.tabs.find((t) => t.title === 'GEO');
          if (geoTab) {
            geoTab.show = false; // hide geo tab
            // If improve campaign, and GEO tab is active, just go to OVERVIEW
            if (this.activeTab === 'GEO') {
              this.setTab('OVERVIEW');
            }
          }
        }
      },
    },
    selectedDateRange: {
      handler(val): void {
        if (val.length === 2) {
          const { startDate, endDate } = normalizeDateRange({
            startDate: val[0],
            endDate: val[1],
          });

          this.startDate = startDate.toISOString();
          this.endDate = endDate.toISOString();
        }
      },
      deep: true,
      immediate: true,
    },
    selectedGroupBy: {
      handler(val): void {
        if (val === 'adset') {
          this.inputGroupBy = 'AD_SET';
        } else if (val === 'ad') {
          this.inputGroupBy = 'AD';
        } else {
          this.inputGroupBy = 'CAMPAIGN';
        }
      },
      immediate: true,
    },
    selectedChildIds: {
      handler(val): void {
        switch (this.selectedGroupBy) {
          case 'adset':
            this.selectedAdSetIds = val;
            this.selectedAdIds = [];
            break;
          case 'ad':
            this.selectedAdIds = val;
            this.selectedAdSetIds = [];
            break;
          default:
            this.selectedAdIds = [];
            this.selectedAdSetIds = [];
        }
      },
      deep: true,
    },
    includeArchivedCampaigns: {
      async handler(val): Promise<void> {
        await this.$nextTick();
        if (!val && this.$route.query.include_archived) {
          await this.$router.push({
            query: Object.assign({}, this.$route.query, {
              include_archived: undefined,
            }),
          });
        } else if (val && !this.$route.query.include_archived) {
          await this.$router.push({
            query: Object.assign({}, this.$route.query, {
              include_archived: true,
            }),
          });
        }
      },
    },
  },
  mounted() {
    if (this.$route.query.include_archived) {
      this.includeArchivedCampaigns = true;
    }
  },
  beforeUpdate() {
    if (this.$route.query.creativeAdId) {
        this.$store.dispatch('advertiser-insights/setSelectedChildIds', [this.$route.query.creativeAdId]);
    }
  },
  methods: {
    setMissingCampaigns(status: boolean): void {
      this.missingCampaigns = status;
    },
    setTab(title: string): void {
      this.tabs.forEach(async (tab) => {
        if (tab.title === title) {
          tab.active = true;
          this.activeTab = tab.title;
          if (
            !this.$route.query.view ||
            (title && this.$route.query.view !== title.toLowerCase())
          ) {
            // To prevent this.$route.query conflicts
            await new Promise((resolve) => setTimeout(resolve, 50));
            await this.$router.push({
              query: Object.assign({}, this.$route.query, {
                view: title.toLowerCase(),
              }),
            });
          }
          return;
        }
        tab.active = false;
      });
    },
    async setGroup(group): Promise<void> {
      // Clear dependant values when changing group
      if (group && this.selectedGroup && group.id !== this.selectedGroup.id) {
        this.$store.dispatch('advertiser-insights/setSelectedCampaign');
      }

      // We need reportSettings from the group, so fetch it here
      if (group.id) {
        const result = await this.$apollo.query({
          query: gql`
            query ($id: ID!) {
              Group(id: $id) {
                id
                name
                reportSettings {
                  domain
                  ecpm
                  landingPageReportSettings
                }
              }
            }
          `,
          variables: {
            id: group.id,
          },
        });
        if (result && result.data && result.data.Group) {
          this.$store.dispatch(
            'advertiser-insights/setSelectedGroup',
            result.data.Group
          );
        }
      }
    },
    setCampaign(campaign): void {
      this.$store.dispatch('advertiser-insights/setSelectedCampaign', campaign);
    },
    setDateRange(dateRange, humanReadable): void {
      this.$store.dispatch('advertiser-insights/setSelectedDateRange', {
        val: dateRange,
        humanReadable,
      });
    },
    async downloadFullReport(): Promise<void> {
      const campaign = this.$route.query.campaign;
      const group = this.$route.query.group;
      // The following may be scary to some, but it's apparently the way people are doing this.
      // So take some deep breaths, and keep on reading....
      if (campaign || group) {
        let url = `${process.env.restApiEndpoint}/reports`;
        if (campaign) {
          url += `/${campaign}/download`;
        } else if (group) {
          url += `/${group}/all-campaigns/download`;
        }
        this.downloadingReport = true;
        let response;
        try {
          response = await this.$axios({
            url,
            params: {
              start_date: this.startDate,
              end_date: this.endDate,
              include_archived: this.includeArchivedCampaigns,
              type: this.activeTab.toLowerCase(),
            },
            method: 'GET',
            responseType: 'blob',
          });
        } catch (e) {
          this.$snotify.error(handleGraphQLErrors(e));
          this.downloadingReport = false;
          return;
        }
        this.downloadingReport = false;
        // Make the blob "downloadable" for the browser
        const file = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = file;
        // Parse the file name from the content-disposition header
        let fileName = 'Report.xlsx';
        const contentDisposition = response.headers['content-disposition'];
        if (contentDisposition) {
          const split = contentDisposition.split(';');
          if (split.length > 1) {
            fileName = split[1].replace(/filename=/, '');
          }
        }
        link.setAttribute('download', decodeURI(fileName));
        document.body.appendChild(link);
        // Browser "download" now
        link.click();
        // Cleanup
        link.remove();
        window.URL.revokeObjectURL(file);
      }
    },
  },
  apollo: {
    stats: {
      query: gql`
        query (
          $groupIds: [ID!]
          $campaignIds: [ID!]!
          $adSetIds: [ID!]
          $adIds: [ID!]
          $startDate: DateTime!
          $endDate: DateTime!
          $groupBy: AdvertiserStatisticsGroupBy
          $resolution: AdvertiserStatisticsResolution
          $includeArchived: Boolean
        ) {
          total: AdvertiserStatistics(
            groupIds: $groupIds
            campaignIds: $campaignIds
            adSetIds: $adSetIds
            adIds: $adIds
            startDate: $startDate
            endDate: $endDate
            resolution: $resolution
            groupBy: $groupBy
            includeArchivedCampaigns: true
            includeArchivedAdSets: true
          ) {
            lastUpdated
            totalStatistics {
              clicks
              impressions
              ctr
              spentLocalCurrency
              ecpm
              localCurrency
              averageTimespent
              conversions
              costPerVisits
              landingRate
              costPerAction
              conversionRate
            }
            totalDailyStatistics {
              date
              clicks
              impressions
              ctr
              spentLocalCurrency
              ecpm
              localCurrency
              averageTimespent
              conversions
              costPerVisits
              landingRate
              costPerAction
              conversionRate
            }
          }
          statistics: AdvertiserStatistics(
            groupIds: $groupIds
            campaignIds: $campaignIds
            adSetIds: $adSetIds
            adIds: $adIds
            startDate: $startDate
            endDate: $endDate
            resolution: $resolution
            groupBy: $groupBy
            includeArchivedCampaigns: $includeArchived
            includeArchivedAdSets: $includeArchived
          ) {
            statistics {
              date
              campaignName
              campaignId
              adSetName
              adSetId
              adId
              clicks
              impressions
              ctr
              adImage
              adTitle
              spentLocalCurrency
              ecpm
              spent
              localCurrency
              averageTimespent
              conversions
              costPerVisits
              landingRate
              costPerAction
              conversionRate
            }
          }
        }
      `,
      variables(): any {
        const campaignIds =
          this.selectedCampaign && this.selectedCampaign.id
            ? [this.selectedCampaign.id]
            : [];
        const groupIds =
          this.selectedGroup && this.selectedGroup.id
            ? [this.selectedGroup.id]
            : [];
        return {
          startDate: this.startDate,
          endDate: this.endDate,
          campaignIds,
          groupIds,
          groupBy: this.inputGroupBy,
          adSetIds: this.selectedAdSetIds,
          adIds: this.selectedAdIds,
          resolution: this.selectedResolution.toUpperCase(),
          includeArchived: this.includeArchivedCampaigns,
        };
      },
      skip(): boolean {
        return (
          (!this.selectedCampaign && !this.selectedGroup) ||
          (this.selectedCampaign &&
            !this.selectedCampaign.id &&
            this.selectedGroup &&
            !this.selectedGroup.id) ||
          !this.inputGroupBy ||
          (!this.startDate && !this.endDate)
        );
      },
      watchLoading(isLoading) {
        this.$store.commit(
          'advertiser-insights/SET_FETCHING_ADVERTISER_STATISTICS',
          isLoading
        );
      },
      debounce: 500,
      notifyOnNetworkStatusChange: true,
      deep: true,
      update(data: any): void {
        const t = data.total;
        const s = data.statistics;
        if (
          s &&
          t &&
          t.totalStatistics &&
          t.totalDailyStatistics &&
          s.statistics
        ) {
          this.$store.commit('advertiser-insights/SET_ACTIVE_FILTERS');
          this.$store.commit(
            'advertiser-insights/SET_LAST_UPDATED',
            t.lastUpdated
          );
          // Get the relevant currency
          const currencies = new Set();
          s.statistics.forEach((i) => {
            currencies.add(i.localCurrency);
          });
          let currency = 'EUR';
          if (currencies.size === 1) {
            const c: string[] = [];
            currencies.forEach((v) => {
              if (typeof v === 'string') {
                c.push(v);
              }
            });
            currency = c[0];
          }
          this.$store.commit(
            'advertiser-insights/SET_TABLE_CURRENCY',
            currency
          );
          if (this.selectedResolution === 'total') {
            this.$store.commit(
              'advertiser-insights/SET_ADVERTISER_STATISTICS',
              {
                total: t.totalStatistics,
                totalDaily: t.totalDailyStatistics,
                totalGroupedBy: s.statistics,
              }
            );
          } else if (this.selectedResolution === 'day') {
            this.$store.commit(
              'advertiser-insights/SET_ADVERTISER_STATISTICS',
              {
                total: t.totalStatistics,
                totalDaily: t.totalDailyStatistics,
                dailyGroupedBy: s.statistics,
              }
            );
          }
        }
      },
    },
  },
});
